<template>
  <v-row class="campos">
    <v-col cols="12" md="3">
      <ValidationProvider name="CEP" rules="required|min:9" v-slot="{ errors }">
        <e-label>CEP</e-label>
        <v-text-field
          v-model="dados[`${prefixo}cep`]"
          v-mask="'#####-###'"
          inputmode="numeric"
          :loading="buscandoCep"
          hint="Preenche o endereço automaticamente"
          :error-messages="errors"
          dense
          solo
        />
      </ValidationProvider>
    </v-col>
    <v-col cols="12" md="7">
      <ValidationProvider name="Logradouro" rules="required|max:160" v-slot="{ errors }">
        <e-label>Logradouro</e-label>
        <v-text-field
          v-model="dados[`${prefixo}logradouro`]"
          counter="160"
          :error-messages="errors"
          dense
          solo
        />
      </ValidationProvider>
    </v-col>
    <v-col cols="12" md="2">
      <ValidationProvider name="Número" rules="numeric|max:9" v-slot="{ errors }">
        <e-label>Número</e-label>
        <v-text-field
          v-model="dados[`${prefixo}numero`]"
          inputmode="numeric"
          hint="Somente dígitos"
          :error-messages="errors"
          dense
          solo
        />
      </ValidationProvider>
    </v-col>
    <v-col cols="12" md="5">
      <ValidationProvider name="Bairro" rules="required|max:64" v-slot="{ errors }">
        <e-label>Bairro</e-label>
        <v-text-field v-model="dados[`${prefixo}bairro`]" :error-messages="errors" dense solo />
      </ValidationProvider>
    </v-col>
    <v-col cols="12" md="2">
      <ValidationProvider name="UF" rules="required" v-slot="{ errors }">
        <e-label>UF</e-label>
        <v-select
          v-model="dados[`${prefixo}uf`]"
          :items="$constants.estadosList"
          item-text="codigo"
          item-value="codigo"
          :error-messages="errors"
          dense
          solo
        />
      </ValidationProvider>
    </v-col>
    <v-col cols="12" md="5">
      <ValidationProvider name="Cidade" rules="required|max:64" v-slot="{ errors }">
        <e-label>Cidade</e-label>
        <v-autocomplete
          v-model="dados[`${prefixo}cidade`]"
          :items="cidades"
          :disabled="!dados[`${prefixo}uf`]"
          :error-messages="errors"
          no-data-text="Nenhuma cidade encontrada"
          dense
          solo
        />
      </ValidationProvider>
    </v-col>
  </v-row>
</template>

<script>
/**
 * Endereço do formulário público, usado pelo pagador (`responsavel_*`) e pelo aluno
 * (`endereco_*`). Os tamanhos são os de `responsavels`, os menores entre as duas tabelas de
 * destino; o número é inteiro lá, por isso só dígitos.
 *
 * O CEP preenche logradouro, bairro, UF e cidade, que continuam editáveis. A cidade é escolhida
 * entre as da UF, como o município de nascimento do aluno.
 */
export default {
  name: "EEnderecoPreMatricula",
  props: {
    dados: { type: Object, required: true },
    // Prefixo dos campos em `dados`: "responsavel_" ou "endereco_".
    prefixo: { type: String, required: true },
  },
  data() {
    return { buscandoCep: false, ultimoCepBuscado: null, temporizadorCep: null };
  },
  computed: {
    cidades() {
      const estado = this.$constants.municipios[this.dados[`${this.prefixo}uf`]];
      return estado ? estado.cidades : [];
    },
  },
  created() {
    // O nome dos campos depende do prefixo, por isso os watchers são criados aqui.
    this.$watch(() => this.dados[`${this.prefixo}uf`], this.aoTrocarUf);
    this.$watch(() => this.dados[`${this.prefixo}cep`], this.aoDigitarCep);
  },
  beforeDestroy() {
    clearTimeout(this.temporizadorCep);
  },
  methods: {
    // Trocar a UF limpa a cidade, exceto quando ela pertence à nova UF — caso do CEP, que
    // preenche UF e cidade juntos.
    aoTrocarUf() {
      if (!this.cidades.includes(this.dados[`${this.prefixo}cidade`])) {
        this.dados[`${this.prefixo}cidade`] = null;
      }
    },
    aoDigitarCep(cep) {
      clearTimeout(this.temporizadorCep);
      const cepInteiro = this.$services.cepService.getCepInteiro(cep);
      if (!cepInteiro || cepInteiro === this.ultimoCepBuscado) return;
      this.temporizadorCep = setTimeout(() => this.buscarCep(cepInteiro), 400);
    },
    async buscarCep(cepInteiro) {
      const campo = (nome) => `${this.prefixo}${nome}`;
      this.buscandoCep = true;
      try {
        const endereco = await this.$services.cepService.buscarDadosDoCep(cepInteiro);
        this.ultimoCepBuscado = cepInteiro;
        this.dados[campo("logradouro")] = endereco.rua || this.dados[campo("logradouro")];
        this.dados[campo("bairro")] = endereco.bairro || this.dados[campo("bairro")];
        this.dados[campo("cidade")] = endereco.cidade || this.dados[campo("cidade")];
        this.dados[campo("uf")] = endereco.estado || this.dados[campo("uf")];
      } catch (error) {
        this.$toast.info(
          "Não foi possível localizar esse CEP. Você pode preencher o endereço manualmente."
        );
      }
      this.buscandoCep = false;
    },
  },
};
</script>
